import { useState, useEffect } from 'react';
import { PlacesService, PlacePhoto } from '@/services/PlacesService';

interface UseGooglePlacePhotosResult {
  photos: PlacePhoto[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch Google Place photos
 * Only fetches when placeId is provided and tab is active
 * @param placeId - Google Place ID
 * @param enabled - Whether to fetch photos
 * @param shareLinkToken - Optional share link token for public access
 * @param sessionId - Optional session ID for public access
 */
export function useGooglePlacePhotos(
  placeId: string | null | undefined,
  enabled: boolean = true,
  shareLinkToken?: string,
  sessionId?: string
): UseGooglePlacePhotosResult {
  const [photos, setPhotos] = useState<PlacePhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const placesService = new PlacesService();

  const fetchPhotos = async () => {
    if (!placeId || !enabled) {
      setPhotos([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await placesService.getPlacePhotos(placeId, 800, shareLinkToken, sessionId);
      setPhotos(data.photos || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load photos';
      setError(errorMessage);
      setPhotos([]);
      console.error('Error fetching place photos:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId, enabled, shareLinkToken, sessionId]);

  return {
    photos,
    isLoading,
    error,
    refetch: fetchPhotos,
  };
}

